import { useCallback } from "react";

import { useProgressNavigation } from "@/components/progress-bar";

import { toast } from "@/lib/use-toast";

import type { CarePlanStep } from "./types";
import { isPatientStep } from "./types";
import {
  useSaveCarePlanStepsMutation,
  useSelectBranchMutation,
  useUpdateCarePlanMutation,
} from "@/query/patient/care-plan";

/**
 * Centralizes patient care plan mutations that were previously inlined
 * in page components: select branch (read-only),
 * and schedule step navigation.
 */
export function useCarePlanMutations(carePlanId: string, patientId: string) {
  const { navigateWithProgress } = useProgressNavigation();
  const selectBranchMutation = useSelectBranchMutation();

  const selectBranch = useCallback(
    async (decisionStepId: string, branchId: string) => {
      try {
        await selectBranchMutation.mutateAsync({
          carePlanId,
          stepId: decisionStepId,
          branchId,
        });
        toast({ title: "Branch selected", variant: "success" });
      } catch {
        toast({ title: "Failed to select branch", variant: "error" });
      }
    },
    [selectBranchMutation, carePlanId],
  );

  const scheduleStep = useCallback(
    (step: CarePlanStep) => {
      const params = new URLSearchParams({
        patientId,
        carePlanId,
        carePlanStepId: step.id,
      });
      if (step.serviceIds.length > 0) {
        params.set("serviceIds", step.serviceIds.join(","));
      }
      const scheduledDate = isPatientStep(step)
        ? step.scheduledDate
        : undefined;
      if (scheduledDate) {
        params.set("start", scheduledDate);
      }
      if (step.description?.trim()) {
        params.set("memo", step.description.trim());
      }
      navigateWithProgress(
        `/check-in/appointment-form/new?${params.toString()}`,
      );
    },
    [navigateWithProgress, patientId, carePlanId],
  );

  return {
    selectBranch,
    scheduleStep,
    saveStepsMutation: useSaveCarePlanStepsMutation(),
    updateMetaMutation: useUpdateCarePlanMutation(),
  };
}
